import { mds } from '@metservicedev/platforms-services-interface/src/services/met-integration';
import type {
    Capability,
    basicDataset,
    datasourceDataset,
    findCapabilitiesByDatasetResponse,
    findDatasetsProps,
    findDatasetsResponse,
    queriesDatasetsFindArgs,
    theLongOneProps,
    theLongOneResponse,
} from '@mono/types/src/mds';
import { usePlatStageContext } from '@mono/use-plat-stage';
import qs from 'qs';
// biome-ignore lint/style/useImportType: <explanation>
import React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

export const MDSContext = createContext<{
    capabilities: Capability[];
    getDatasets: (props: queriesDatasetsFindArgs) => Promise<{
        'basic-datasets': basicDataset[];
        'datasource-datasets': datasourceDataset[];
    }>;
    findCapabilitiesByDataset: (datasetId: string, signal?: AbortSignal) => Promise<findCapabilitiesByDatasetResponse>;
    theLongOne: (props: theLongOneProps, signal?: AbortSignal) => Promise<theLongOneResponse>;
    findDatasets: (props: findDatasetsProps, signal?: AbortSignal) => Promise<findDatasetsResponse>;
}>({
    capabilities: [],
    getDatasets: () => Promise.resolve({ 'basic-datasets': [], 'datasource-datasets': [] }),
    findCapabilitiesByDataset: () => Promise.resolve({ capability_endpoint_ids: {}, capabilities: [], endpoints: [] }),
    findDatasets: () => Promise.resolve({ 'basic-datasets': [], 'datasource-datasets': [] }),
    theLongOne: () => Promise.resolve({ capability_dataset_endpoint_ids: {}, capabilities: [], datasets: [] }),
});

export const useMDSContext = () => useContext(MDSContext);

interface MDSContextProviderProps {
    metjwt?: string;
    children: React.ReactNode;
}
export const MDSContextProvider = ({ metjwt, children }: MDSContextProviderProps) => {
    const cookieToken = document.cookie.split('TOKEN=')[1];
    const { stage } = usePlatStageContext();
    const [capabilities, setCapabilities] = useState<Capability[]>([]);

    const headers = {
        ...(metjwt ? { Authorization: `Bearer ${metjwt}` } : undefined),
        ...(cookieToken ? { 'x-cookie-token': cookieToken } : undefined),
    };

    useEffect(() => {
        const urlToUse =
            metjwt || cookieToken ? `${mds[stage]}/v1/capabilities` : `https://mov-integration-mds-${stage}-capabilities.s3.amazonaws.com/capabilities.json`;
        fetch(urlToUse, {
            headers,
        })
            .then((r) => {
                return r.json();
            })
            .then(setCapabilities);
    }, []);

    const getDatasets = async (props: queriesDatasetsFindArgs) => {
        if (!metjwt) {
            console.log('mds context needs metjwt');
            return;
        }

        const queryString = qs.stringify(props, { arrayFormat: 'comma' });
        const response = await fetch(mds[stage] + '/v1/queries/datasets/find?' + queryString, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${metjwt}`,
            },
        });

        const datasets = await response.json();
        return datasets;
    };

    const findCapabilitiesByDataset = async (datasetId: string, signal: AbortSignal) => {
        if (!metjwt) {
            console.log('mds context needs metjwt');
            return;
        }

        const response = await fetch(mds[stage] + '/v1/queries/capabilities/findByDataset?show_objects=true&dataset_id=' + datasetId, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${metjwt}`,
            },
            signal,
        });
        const actualResponse = await response.json();

        return actualResponse;
    };

    const theLongOne = async (props: theLongOneProps, signal: AbortSignal) => {
        if (!metjwt) {
            console.log('mds context needs metjwt');
            return;
        }

        const queryString = qs.stringify(props, { arrayFormat: 'comma' });
        const response = await fetch(mds[stage] + '/v1/queries/datasets/findByCapabilityDataStructureAndMimeType?' + queryString, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${metjwt}`,
            },
            signal,
        });

        const datasets = await response.json();
        return datasets;
    };

    const findDatasets = async (props: findDatasetsProps, signal?: AbortSignal) => {
        if (!metjwt) {
            console.log('mds context needs metjwt');
            return;
        }

        const queryString = qs.stringify(props, { arrayFormat: 'comma' });
        const response = await fetch(mds[stage] + '/v1/queries/datasets/find?' + queryString, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${metjwt}`,
            },
            signal,
        });

        const datasets = await response.json();
        return datasets;
    };

    return <MDSContext.Provider value={{ capabilities, getDatasets, findCapabilitiesByDataset, theLongOne, findDatasets }}>{children}</MDSContext.Provider>;
};
